import React, { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { initializeSocketListeners } from "../socket.listener/socket";

import { AdminRoute } from "./AdminRoute";
import { SuperAdminRoute } from "./SuperAdminRoute";
import { ProtectedDesktopRoute } from "./ProtectedDesktopRoute";
import { RequireAccount } from "./RequireAccount";

// ================================
// Lazy imports - default exports
// ================================
const HomePage = lazy(() => import("../pages/home-page"));
const MarketingPage = lazy(() => import("../pages/MarketingPage"));
const LayoutPages = lazy(() => import("../pages/layoutPages"));
const SalesPage = lazy(() => import("../pages/SalesPage"));
const PdPage = lazy(() => import("../pages/PDPage"));
const PmoPage = lazy(() => import("../pages/PMOPage"));
const TiPage = lazy(() => import("../pages/TiPages"));
const RhPage = lazy(() => import("../pages/RhPage"));
const RetailPage = lazy(() => import("../pages/RetailPage"));
const MidiaPage = lazy(() => import("../pages/MidiaPage"));
const ConstructionPage = lazy(() => import("../pages/ConstructionPage"));
const AutomationsPage = lazy(() => import("../pages/AutomationsPage"));
const Dashboard = lazy(() => import("../pages/DashoboardsPage"));
const WorkFormsPage = lazy(() => import("../pages/WorkFormsPage"));
const ColumnsPage = lazy(() => import("../pages/ColumnsPage"));
const RealTimePage = lazy(() => import("../pages/RealTimePage"));
const MultiCompaniesPage = lazy(() => import("../pages/MultiCompaniesPage"));
const FiltersPage = lazy(() => import("../pages/FiltersPage"));
const AuditPage = lazy(() => import("../pages/AuditPage"));
const ImportDataPage = lazy(() => import("../pages/ImportDataPage"));
const MindMapPage = lazy(() => import("../pages/MindMapPage"));
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("../pages/TermsOfServicePage"));
// ================================
// Lazy imports - named exports
// ================================
const LoginSignup = lazy(() =>
  import("../pages/login-signup").then((module) => ({
    default: module.LoginSignup,
  }))
);

const Desktop = lazy(() =>
  import("../pages/desktop").then((module) => ({
    default: module.Desktop,
  }))
);

const UserAccount = lazy(() =>
  import("../pages/userAccount").then((module) => ({
    default: module.UserAccount,
  }))
);

const UserCreate = lazy(() =>
  import("../pages/user-create").then((module) => ({
    default: module.UserCreate,
  }))
);

const Register = lazy(() =>
  import("../pages/register").then((module) => ({
    default: module.Register,
  }))
);

const Admin = lazy(() =>
  import("../pages/userAdmin").then((module) => ({
    default: module.Admin,
  }))
);

const AdminSuper = lazy(() =>
  import("../pages/superAdmin").then((module) => ({
    default: module.AdminSuper,
  }))
);

const FormPage = lazy(() =>
  import("../pages/formPage").then((module) => ({
    default: module.FormPage,
  }))
);

function PageFallback() {
  const wordRef = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    const el = wordRef.current;
    if (!el) return;

    const text = el.getAttribute("data-text") || el.textContent || "LOADING...";
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+{}[]<>?/\\|";
    const cyclesPerChar = 5;
    const frameDelay = 32;

    let letterIdx = 0;
    let cycle = 0;
    let stopped = false;

    el.textContent = "";

    const spans = [...text].map((ch) => {
      const s = document.createElement("span");
      s.dataset.orig = ch;
      s.textContent = ch === " " ? " " : "-";
      el.appendChild(s);
      return s;
    });

    function clearTimers() {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    }

    function addTimer(callback, delay) {
      const id = setTimeout(callback, delay);
      timersRef.current.push(id);
      return id;
    }

    function tick() {
      if (stopped) return;

      for (let i = letterIdx; i < spans.length; i++) {
        const sp = spans[i];

        if (!sp || sp.dataset.orig === " ") continue;

        sp.textContent = charset[(Math.random() * charset.length) | 0];
        sp.style.opacity = String(0.6 + Math.random() * 0.4);
      }

      if (cycle < cyclesPerChar) {
        cycle++;
      } else if (letterIdx < spans.length) {
        const curr = spans[letterIdx];

        if (curr) {
          curr.textContent = curr.dataset.orig;
          curr.style.opacity = "1";
          curr.classList.add("done");
        }

        letterIdx++;
        cycle = 0;
      } else {
        addTimer(reset, 750);
        return;
      }

      addTimer(tick, frameDelay);
    }

    function reset() {
      if (stopped) return;

      clearTimers();

      letterIdx = 0;
      cycle = 0;

      spans.forEach((s) => {
        s.classList.remove("done");
        s.textContent = s.dataset.orig === " " ? " " : "-";
        s.style.opacity = "0.9";
      });

      tick();
    }

    reset();

    return () => {
      stopped = true;
      clearTimers();
      spans.length = 0;
    };
  }, []);

  return (
    <div className="route-preload-root" aria-label="Carregando Upsuite">
      <div className="splash loading-splash">
        <div className="word-row">
          <img
            src="/logo.png"
            alt="Upsuite"
            className="brand-mark"
            width="60"
            height="60"
          />

          <div ref={wordRef} className="route-word" data-text="LOADING...">
            LOADING...
          </div>
        </div>

        <div className="overlay" aria-hidden="true" />
      </div>
    </div>
  );
}

export function RootCmp() {
  const account = useSelector(
    (store) => store.accounts?.selectedAccount?.accountId
  );

  useEffect(() => {
    if (account?._id) {
      initializeSocketListeners(account._id);
    }
  }, [account]);

  const DesktopProtected = useMemo(
    () => (
      <RequireAccount>
        <Desktop />
      </RequireAccount>
    ),
    []
  );

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />

        <Route element={<LayoutPages />}>
          <Route path="/marketing" element={<MarketingPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/pd" element={<PdPage />} />
          <Route path="/pmo" element={<PmoPage />} />
          <Route path="/ti" element={<TiPage />} />
          <Route path="/rh" element={<RhPage />} />
          <Route path="/retail" element={<RetailPage />} />
          <Route path="/midia" element={<MidiaPage />} />
          <Route path="/construction" element={<ConstructionPage />} />

          <Route path="/automations" element={<AutomationsPage />} />
          <Route path="/dashboards" element={<Dashboard />} />
          <Route path="/workForms" element={<WorkFormsPage />} />
          <Route path="/columns" element={<ColumnsPage />} />
          <Route path="/realTime" element={<RealTimePage />} />
          <Route path="/multiCompanies" element={<MultiCompaniesPage />} />
          <Route path="/filters" element={<FiltersPage />} />
          <Route path="/auditoriums" element={<AuditPage />} />
          <Route path="/importData" element={<ImportDataPage />} />
          <Route path="/mindMap" element={<MindMapPage />} />
        </Route>

        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/desktop" element={<Navigate to="/home" replace />} />

        <Route path="/auth/login" element={<LoginSignup />} />
        <Route path="/auth/signup/success" element={<LoginSignup />} />
        <Route path="/auth/signup" element={<LoginSignup />} />

        <Route path="/userAccount" element={<UserAccount />} />

        <Route path="/home" element={DesktopProtected} />
        <Route path="/helper" element={DesktopProtected} />

        <Route
          path="/desktop/info/:desktopId"
          element={
            <ProtectedDesktopRoute>
              <Desktop />
            </ProtectedDesktopRoute>
          }
        />

        <Route
          path="/desktop/board/:boardId"
          element={
            <ProtectedDesktopRoute>
              <Desktop />
            </ProtectedDesktopRoute>
          }
        />

        <Route
          path="/desktop/board/:boardId/:groupId/:taskId"
          element={
            <ProtectedDesktopRoute>
              <Desktop />
            </ProtectedDesktopRoute>
          }
        />

        <Route path="/userCreate" element={<UserCreate />} />
        <Route path="/register" element={<Register />} />

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />

        <Route
          path="/stripe-payment/success-checkout-session/:sessionId"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />

        <Route
          path="/superAdmin"
          element={
            <SuperAdminRoute>
              <AdminSuper />
            </SuperAdminRoute>
          }
        />

        <Route path="/forms/:prefix/:slug" element={<FormPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}